<template>
  <v-container class="periodic">
    <template>
      <ViewHeader :title="viewTitle" class="mb-2">
        <div class="d-flex justify-space-between align-center flex-grow-1">
          <VideoButton
            class="ml-3 mr-8"
            @click="showVideoTutorial(viewEnum.POSITION_ALL_LOCATIONS)"
          />
          <div class="d-flex justify-end">
            <PeriodPicker
              ref="period-picker"
              :single-period="isTableRoute"
              @update="onDateUpdated"
              inputClasses="mx-3"
            />
            <SelectedFacilities
              :selectedCount="(gmbAddressesIds && gmbAddressesIds.length) || 0"
              :allSelected="areAllFacilitiesSelected"
              :groupsCount="
                (facilitiesGroupsIds && facilitiesGroupsIds.length) || 0
              "
              :citiesCount="(cities && cities.length) || 0"
              :loading="loading.filter"
              @change="dialog.filter = true"
            />
          </div>
        </div>
      </ViewHeader>
    </template>
    <template>
      <v-card>
        <v-tabs slider-color="primary" class="periodic__tabs">
          <v-tab
            v-for="tab in tabsWithAccess"
            :key="tab.id"
            :to="tab.route"
            ripple
          >
            {{ $t(tab.label) }}
          </v-tab>
        </v-tabs>
        <v-tabs-items @change="updateRouter($event)">
          <v-row no-gutters justify="center">
            <v-col>
              <header class="periodic__subheader">
                <div class="periodic__subheader-primary">
                  <div class="d-flex mb-4 align-center">
                    <h3>
                      {{
                        $t(
                          `message.analytics.periodic.types.${selectedReportType}`
                        )
                      }}
                    </h3>
                    <v-tooltip bottom :max-width="500">
                      <template v-slot:activator="{ on }">
                        <div v-on="on" class="ml-3">
                          <v-icon color="lime" size="18">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          `message.analytics.periodic.tooltip.${selectedReportType}`
                        )
                      }}
                    </v-tooltip>
                  </div>
                  <KeywordsSelector
                    ref="keywords-selector"
                    :keywords="keywords"
                    :selected-report-type="selectedReportType"
                    :isTableRoute="isTableRoute"
                    :export-ready="loading.export"
                    @save="saveKeywords"
                    class="mb-2"
                  />
                </div>
                <div class="periodic__subheader-secondary">
                  <ReportTypeSelector
                    :selected-type="selectedReportType"
                    :general-types="generalChartTypes"
                    :top-chart-series="topChartSeries"
                    :is-table-route="isTableRoute"
                    :is-chart-route="isChartRoute"
                    :is-grouped-data="table && table.grouped"
                    :searching-modes="searchingModes"
                    @selected="selectReportType"
                    @toggleCheckbox="toggleCheckboxByValue"
                    @changeSwitch="changeIsGroupedDataSwitch"
                  >
                  </ReportTypeSelector>
                  <ui-button
                    v-if="showExportButton"
                    :loading="loading.export"
                    color="primary"
                    class="ml-2"
                    outlined
                    @click="exportToCsv"
                  >
                    {{ $t("message.common.exportToCSV") }}
                  </ui-button>
                </div>
              </header>

              <transition name="fade" mode="out-in">
                <router-view
                  v-bind="childProps"
                  @tablePagination="handleChange"
                  @setLoading="setLoading"
                  @sort="sortTable"
                  class="animation-duration--250"
                ></router-view>
              </transition>
            </v-col>
          </v-row>
        </v-tabs-items>
      </v-card>
    </template>
    <SectionLoader :status="loading.view" :opacity="1" />

    <FilterDialog
      v-if="dialog.filter"
      :gmb-addresses-ids="gmbAddressesIds"
      :cities="cities"
      :facilities-groups="facilitiesGroupsIds"
      :chosen-search-type="searchType"
      disable-area-facilities
      @setSearchType="searchType = $event"
      @change="onFiltersChange({ ...$event })"
      :handleClose="closeFilterDialog"
    />
    <AsyncDownloadDialog
      v-if="showAsyncDownloadDialog"
      :export-id="exportId"
      @close="showAsyncDownloadDialog = false"
    />
  </v-container>
</template>

<script>
import { mapState } from "vuex";

import cuid from "cuid";
import { permissionCheck } from "@/helpers/permission-check";
import { Permission } from "@/enums/permission";

import ViewHeader from "@/components/ui/ViewHeader";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import PeriodPicker from "@/components/Analytics/Periodic/PeriodPicker";
import SelectedFacilities from "@/components/Analytics/Periodic/SelectedFacilities";
import KeywordsSelector from "@/components/Analytics/Periodic/KeywordsSelector";
import ReportTypeSelector from "@/components/Analytics/Periodic/ReportTypeSelector";
import FilterDialog from "@/components/Filter/dialog";
import AsyncDownloadDialog from "@/components/AsyncDownloadDialog";

import {
  PERIODIC_REPORT_TYPE,
  TOP_CHART_SERIE
} from "@/enums/charts-type.enum";
import { FACILITIES_FILTER_SEARCH_TYPES } from "@/enums/facilty-details-status.enum";
import { APP_COLORS } from "@/config/app-colors";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

import serviceHandler from "@/helpers/handlers/service";
import facilitiesService from "@/services/facilities";
import periodicService from "@/services/periodic";
import { videoTutorialsMixin } from "@/helpers/mixins/video-tutorials";
import VideoButton from "@/components/ui/VideoButton.vue";

export default {
  name: "PeriodicRankingsView",
  components: {
    ViewHeader,
    SectionLoader,
    PeriodPicker,
    SelectedFacilities,
    KeywordsSelector,
    ReportTypeSelector,
    FilterDialog,
    VideoButton,
    AsyncDownloadDialog
  },
  mixins: [videoTutorialsMixin],
  data() {
    return {
      viewTitle: this.$t("message.analytics.periodic.title"),
      loading: {
        view: false,
        chart: false,
        table: false,
        filter: false,
        export: false
      },
      loaded: false,
      dialog: {
        filter: false
      },

      tabs: [
        {
          id: cuid(),
          label: "message.analytics.periodic.table.tabTitle",
          route: { name: "analytics-search-ranking-table" },
          access: permissionCheck(Permission.VIEW_GMB_INSIGHTS)
        },
        {
          id: cuid(),
          label: "message.analytics.periodic.chart.tabTitle",
          route: { name: "analytics-search-ranking-chart" },
          access: permissionCheck(Permission.VIEW_GMB_INSIGHTS)
        }
      ],

      date: [],
      periodType: null,

      totalFacilitiesCount: 0,
      gmbAddressesIds: [],
      cities: [],
      facilitiesGroupsIds: [],
      searchType: null,

      keywords: [],

      chart: {
        data: [],
        labels: []
      },

      table: {
        page: 1,
        perPage: null,
        perPageOptions: [10, 25, 50, 100],
        totalCount: 0,
        grouped: false,
        data: [],
        summary: null,
        orderBy: null,
        orderDirection: null
      },

      selectedReportType: PERIODIC_REPORT_TYPE.AVERAGE,
      generalChartTypes: [
        {
          label: this.$t(
            `message.analytics.periodic.types.${PERIODIC_REPORT_TYPE.TOP}`
          ),
          value: PERIODIC_REPORT_TYPE.TOP
        },
        {
          label: this.$t(
            `message.analytics.periodic.types.${PERIODIC_REPORT_TYPE.AVERAGE}`
          ),
          value: PERIODIC_REPORT_TYPE.AVERAGE
        }
      ],
      topChartSeries: [
        {
          label: this.$t(`message.topChartSeries.${TOP_CHART_SERIE.TOP_3}`),
          color: APP_COLORS.TEAL,
          value: TOP_CHART_SERIE.TOP_3,
          state: true
        },
        {
          label: this.$t(`message.topChartSeries.${TOP_CHART_SERIE.TOP_10}`),
          color: APP_COLORS.PINK,
          value: TOP_CHART_SERIE.TOP_10,
          state: true
        },
        {
          label: this.$t(`message.topChartSeries.${TOP_CHART_SERIE.TOP_20}`),
          color: APP_COLORS.ACCENT,
          value: TOP_CHART_SERIE.TOP_20,
          state: true
        }
      ],
      showAsyncDownloadDialog: false,
      exportId: null
    };
  },

  async created() {
    this.loading.view = true;
    this.resetTablePagination();
    await this.fetchAllFacilities();
    await this.fetchAndSetInitialKeywords();
    this.loading.view = false;
    this.loaded = true;
    this.handleUpdateRouteData();
  },

  computed: {
    ...mapState({
      storeKeywords: state => state.keywords.keywords
    }),
    childProps() {
      return {
        loading: this.loading,
        keywords: this.keywords,
        chart: this.chart,
        table: this.table,
        selectedReportType: this.selectedReportType,
        dataType: this.searchType,
        maxValueY: this.chartMaxValueY
      };
    },
    tabsWithAccess() {
      return this.tabs.filter(tab => tab.access);
    },
    keywordsIds() {
      return this.keywords.map(({ keyword }) => keyword.id);
    },
    areAllFacilitiesSelected() {
      return this.gmbAddressesIds.length === this.totalFacilitiesCount;
    },

    isTableRoute() {
      return this.$route.name === this.tabs[0].route.name;
    },
    isChartRoute() {
      return this.$route.name === this.tabs[1].route.name;
    },

    isTopReport() {
      return this.selectedReportType === PERIODIC_REPORT_TYPE.TOP;
    },
    isAverageReport() {
      return this.selectedReportType === PERIODIC_REPORT_TYPE.AVERAGE;
    },

    isFacilitiesSearch() {
      return !this.isCitiesSearch && !this.isGroupsSearch;
    },
    isCitiesSearch() {
      return (
        this.cities && Array.isArray(this.cities) && this.cities.length > 0
      );
    },
    isGroupsSearch() {
      return (
        this.facilitiesGroupsIds &&
        Array.isArray(this.facilitiesGroupsIds) &&
        this.facilitiesGroupsIds.length > 0
      );
    },
    searchingModes() {
      return {
        facilities: this.isFacilitiesSearch,
        cities: this.isCitiesSearch,
        groups: this.isGroupsSearch
      };
    },

    topChartSeriesStates() {
      return this.topChartSeries.map(({ state }) => state);
    },
    chartMaxValueY() {
      if (
        !Array.isArray(this.gmbAddressesIds) ||
        !Array.isArray(this.keywordsIds)
      ) {
        return 0;
      }

      return this.isTopReport
        ? this.gmbAddressesIds.length * this.keywordsIds.length
        : this.gmbAddressesIds.length;
    },
    selectedData() {
      if (this.isCitiesSearch) {
        return { component: "cities", num_selected: this.cities.length };
      } else if (this.isGroupsSearch) {
        return {
          component: "facilitiesGroups",
          num_selected: this.facilitiesGroupsIds.length
        };
      } else {
        return {
          component: "facilities",
          num_selected: this.gmbAddressesIds.length
        };
      }
    },
    fetchTableDataPayload() {
      const payload = {
        page: this.table.page,
        perPage: this.table.perPage,
        keywords: this.keywordsIds,
        period: this.periodType,
        report_type: this.selectedReportType,
        is_grouped: this.isGroupsSearch ? this.table.grouped : false,
        date: this.date ? this.date[0] : null,
        ...(this.table.orderBy && { order_by: this.table.orderBy }),
        ...(this.table.orderDirection && {
          order_direction: this.table.orderDirection
        })
      };

      if (this.isCitiesSearch) {
        payload.cities = this.cities;
      } else if (this.isGroupsSearch) {
        payload.groups = this.facilitiesGroupsIds;
      } else {
        payload.gmb_address_ids = this.gmbAddressesIds;
      }
      return payload;
    },
    showExportButton() {
      return this.$route.name === "analytics-search-ranking-table";
    }
  },

  watch: {
    keywords() {
      this.handleUpdateRouteData();
    },
    gmbAddressesIds() {
      this.handleUpdateRouteData();
    },
    date() {
      this.handleUpdateRouteData();
    },
    selectedReportType() {
      this.handleUpdateRouteData();
    },
    topChartSeriesStates() {
      this.handleUpdateRouteData();
    },
    "table.grouped"() {
      if (this.isTableRoute) {
        this.fetchTableData();
      }
    },
    $route: async function(route) {
      await this.$nextTick();
      this.checkResetKeywords();

      const routeHandlers = {
        [this.tabs[0].route.name]: () => {
          this.chart.data = [];
          this.chart.labels = [];
          this.$refs["period-picker"].updateDateByRouteChange();
        },
        [this.tabs[1].route.name]: () => {
          this.table.data = [];
          this.$refs["period-picker"].updateDateByRouteChange();
        }
      };

      if (route.name in routeHandlers) {
        routeHandlers[route.name]();
      }
    }
  },

  methods: {
    async sortTable({ keywordId, orderDirection }) {
      this.table.orderBy = keywordId;
      this.table.orderDirection = orderDirection;
      await this.fetchTableData();
    },

    async exportToCsv() {
      try {
        this.loading.export = true;

        const { export_id } = await serviceHandler(
          periodicService.periodicRankingsTableExport(
            this.fetchTableDataPayload
          )
        );
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.ANALYTICS_GM_POSITION_TABLE_EXPORT,
          format: "csv",
          date_from: this.date ? this.date[0] : null,
          date_to: this.date ? this.date[1] : null,
          num_keywords: this.keywordsIds.length,
          input_type: this.isTopReport ? "top_positions" : "avg_position",
          ...this.selectedData
        });
        this.loading.export = false;

        this.exportId = export_id;
        this.showAsyncDownloadDialog = true;
      } catch (err) {
        this.loading.export = false;
      }
    },
    async fetchChartData() {
      if (this.keywordsIds.length === 0) return;

      this.loading.chart = true;

      const payload = {
        keywords: this.keywordsIds,
        period: this.periodType,
        report_type: this.selectedReportType,
        date_from: this.date ? this.date[0] : null,
        date_to: this.date ? this.date[1] : null
      };

      if (this.isCitiesSearch) {
        payload.cities = this.cities;
      } else if (this.isGroupsSearch) {
        payload.groups = this.facilitiesGroupsIds;
      } else {
        payload.gmb_address_ids = this.gmbAddressesIds;
      }

      const response = await serviceHandler(
        periodicService.postPeriodicChart(payload)
      );

      if (response && response.length && response[0].results) {
        const data = response[0].results;
        this.setChartData(data);

        // GTM event data collection below
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.ANALYTICS_GM_POSITION_CHART_DISPLAYED,
          date_from: this.date ? this.date[0] : null,
          date_to: this.date ? this.date[1] : null,
          num_selected:
            (this.cities && this.cities.length) ||
            (this.facilitiesGroupsIds && this.facilitiesGroupsIds.length) ||
            (this.gmbAddressesIds && this.gmbAddressesIds.length),
          component:
            this.searchType || FACILITIES_FILTER_SEARCH_TYPES.FACILITIES,
          num_keywords: this.keywordsIds.length,
          input_type: this.isTopReport ? "top_positions" : "avg_position",
          fields: this.isTopReport
            ? this.topChartSeries
                .filter(({ state }) => state)
                .map(({ value }) => value)
            : null
        });
        // end GTM event stuff
      }

      if (
        (this.chart && this.chart.data && this.chart.data.length === 0) ||
        (this.chart && this.chart.labels && this.chart.labels.length === 0)
      ) {
        // in other cases loading is switched off by chart event on rendering finished
        this.loading.chart = false;
      }
    },

    async fetchTableData(resetPaginationAndSummary = true) {
      if (this.keywordsIds.length === 0) return;

      if (resetPaginationAndSummary) {
        this.resetTablePagination();
        this.table.summary = null;
      }

      this.loading.table = true;

      const response = await serviceHandler(
        periodicService.postPeriodicTable(this.fetchTableDataPayload)
      );

      if (this.table.summary === null) {
        // in order to avoid fetching summary data e.g. on pagination change
        this.fetchTableDataSummary(this.fetchTableDataPayload);
      }

      if (response) {
        this.table.data = response.results;
        this.table.totalCount = response.count;

        // GTM event data collection below
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.ANALYTICS_GM_POSITION_TABLE_DISPLAYED,
          date_from: this.date ? this.date[0] : null,
          date_to: this.date ? this.date[1] : null,
          num_selected:
            (this.cities && this.cities.length) ||
            (this.facilitiesGroupsIds && this.facilitiesGroupsIds.length) ||
            (this.gmbAddressesIds && this.gmbAddressesIds.length),
          component:
            this.searchType || FACILITIES_FILTER_SEARCH_TYPES.FACILITIES,
          num_keywords: this.keywordsIds.length,
          input_type: this.isTopReport ? "top_positions" : "avg_position",
          fields:
            this.isGroupsSearch && this.table.grouped
              ? "grouped_locations"
              : null
        });
        // end GTM event stuff
      }

      this.loading.table = false;
    },

    async fetchTableDataSummary(payload) {
      const summaryResponse = await serviceHandler(
        periodicService.postPeriodicTableSummary(payload)
      );

      if (summaryResponse) {
        if (this.table.grouped) {
          this.table.summary = {
            facility_group: {
              id: cuid(),
              name: this.$t("message.analytics.periodic.table.summary"),
              facilities_count: this.gmbAddressesIds.length
            },
            ...summaryResponse
          };
        } else {
          this.table.summary = {
            gmb_address: {
              id: cuid(),
              name: this.$t("message.analytics.periodic.table.summary"),
              formatted_address: this.$t(
                "message.analytics.periodic.table.summarySecondaryText"
              )
            },
            ...summaryResponse
          };
        }
      }
    },

    async fetchAndSetInitialKeywords() {
      await this.$store.dispatch("keywords/fetchKeywords", { all: true });
      this.keywords = [...this.storeKeywords];
    },

    async handleChange({ page, perPage }) {
      this.table.page = page;
      this.table.perPage = perPage;
      await this.fetchTableData(false);
    },

    async fetchAllFacilities() {
      this.loading.filter = true;
      const ids = await serviceHandler(
        facilitiesService.getFacilitiesIds({
          is_owner: true
        })
      );
      if (ids) {
        this.totalFacilitiesCount = ids.length;

        this.gmbAddressesIds = ids.map(
          ({ gmb_address_id: gmbAddressId }) => gmbAddressId
        );
      }
      this.loading.filter = false;
    },

    async fetchFacilitiesIds() {
      const params = {
        isOwner: true,
        cities: this.cities,
        groups: this.facilitiesGroupsIds
      };

      const addressesIds = await serviceHandler(
        facilitiesService.getFacilitiesIds(params)
      );

      this.gmbAddressesIds = addressesIds.map(
        ({ gmb_address_id: gmbAddressId }) => gmbAddressId
      );
    },

    saveKeywords(keywords) {
      this.keywords = keywords;
    },

    selectReportType(selectedReportType) {
      if (this.selectedReportType !== selectedReportType) {
        this.selectedReportType = selectedReportType;

        this.checkResetKeywords();
      }
    },

    checkResetKeywords() {
      if (
        this.isAverageReport &&
        this.isChartRoute &&
        this.keywords &&
        this.keywords.length > 1
      ) {
        this.keywords = [];
        this.chart.data = [];
        this.chart.labels = [];
        this.$refs["keywords-selector"].setDialog("keywords", true);
      }
    },

    toggleCheckboxByValue(value) {
      const index = this.topChartSeries.findIndex(
        serie => serie.value === value
      );

      if (index >= 0) {
        this.topChartSeries[index].state = !this.topChartSeries[index].state;
      }
    },

    changeIsGroupedDataSwitch(value) {
      this.table.grouped = value;
    },

    onDateUpdated({ date, type }) {
      this.date = date;
      this.periodType = type;
    },

    async onFiltersChange({ gmbAddressesIds, cities, facilitiesGroupsIds }) {
      this.loading.view = true;

      if (
        this.searchType === FACILITIES_FILTER_SEARCH_TYPES.FACILITIES &&
        gmbAddressesIds &&
        gmbAddressesIds.length
      ) {
        this.gmbAddressesIds = gmbAddressesIds;
        this.cities = [];
        this.facilitiesGroupsIds = [];
      } else if (
        this.searchType === FACILITIES_FILTER_SEARCH_TYPES.CITY &&
        cities &&
        cities.length
      ) {
        this.cities = cities;
        this.facilitiesGroupsIds = [];
        await this.fetchFacilitiesIds();
      } else if (
        this.searchType === FACILITIES_FILTER_SEARCH_TYPES.FACILITIES_GROUPS &&
        facilitiesGroupsIds &&
        facilitiesGroupsIds.length
      ) {
        this.cities = [];
        this.facilitiesGroupsIds = facilitiesGroupsIds;
        await this.fetchFacilitiesIds();
      }

      this.loading.view = false;
    },

    async setChartData(response) {
      return new Promise(async (resolve, reject) => {
        if (!response) return reject();

        this.chart.data = [];
        this.chart.labels = [];

        if (this.isTopReport) {
          this.setTopChartData(response);
        } else {
          this.setAverageChartData(response);
        }
        resolve();
      });
    },

    getChartLabels(serieData) {
      return serieData.map(({ date }) => date);
    },

    setTopChartData(response) {
      for (const [chartSerie, serieData] of Object.entries(response)) {
        const chartSerieConfig = this.topChartSeries.find(
          ({ value }) => value === chartSerie
        );

        const lineData = {
          color: chartSerieConfig ? chartSerieConfig.color : "black",
          name: chartSerieConfig ? chartSerieConfig.label : "Top",
          visible: chartSerieConfig ? chartSerieConfig.state : false,
          data: []
        };

        if (Array.isArray(serieData) && serieData.length > 0) {
          lineData.data = serieData.map(({ value }) => value);

          if (this.chart.labels.length === 0) {
            // set chart labels only once
            this.chart.labels = this.getChartLabels(serieData);
          }
        }

        this.chart.data.push(lineData);
      }
    },

    setAverageChartData(response) {
      const lineData = {
        color: APP_COLORS.PRIMARY,
        name: this.$t(
          `message.analytics.periodic.types.${PERIODIC_REPORT_TYPE.AVERAGE}`
        ),
        visible: true,
        data: []
      };

      if (Array.isArray(response) && response.length > 0) {
        lineData.data = response.map(({ value }) => value);

        if (this.chart.labels.length === 0) {
          // set chart labels only once
          this.chart.labels = this.getChartLabels(response);
        }
      }

      this.chart.data.push(lineData);
    },

    handleUpdateRouteData() {
      if (!this.loaded) return;
      this.checkResetKeywords();

      if (this.isChartRoute) {
        this.fetchChartData();
      } else if (this.isTableRoute) {
        this.fetchTableData();
      }
    },

    resetTablePagination() {
      this.table.page = 1;
      this.table.perPage = this.table.perPageOptions[0];
    },

    setLoading(type, value) {
      if (type in this.loading) {
        this.loading[type] = value;
      }
    },

    closeDialog(type) {
      if (type in this.dialog) {
        this.dialog[type] = false;
      }
    },

    closeFilterDialog() {
      this.dialog.filter = false;
    },

    updateRouter(val) {
      this.$router.push(val);
    }
  }
};
</script>

<style lang="scss" scoped>
.periodic {
  &__tabs {
    border-bottom: 1px solid $gray-200;
    padding: 0 $spacing * 3;
  }

  &__subheader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: $spacing * 3 $spacing * 3 0;
    flex-direction: column;
    margin-bottom: $spacing * 2;
    &-primary,
    &-secondary {
      width: 100%;
    }

    &-secondary {
      margin-top: $spacing * 4;
      display: flex;
      justify-content: space-between;

      ::v-deep .wrapper {
        min-height: auto;
      }
    }
  }
}
</style>
